// Live analysis screen: reading the orchestrator's frame stream.
//
// The stream is a plain chunked HTTP response, not a WebSocket, and that shapes
// this file. There is nothing to send - a fetch() body reader has nowhere to
// write - so "view only" is a property of the transport rather than a rule this
// code follows. Session cookies authenticate it like any other request.
//
// Wire format, repeated until the connection ends. All integers big-endian,
// mirroring internal/transport/httpapi/submission_screen.go:
//
//   uint8  flags          bit 0 set = keyframe
//   uint16 x, y           position of this piece within the screen
//   uint16 width, height  size of this piece
//   uint16 screenWidth    full framebuffer size
//   uint16 screenHeight
//   uint16 inputWidth     the coordinate space input on this picture is
//   uint16 inputHeight    expressed in, when not the screen's own (0 = same)
//   uint32 jpegLen        bytes of JPEG that follow (0 = keepalive)

/** Bytes of fixed header before each frame's payload. */
const FRAME_HEADER_LEN = 21;

/** One decoded piece of the guest's screen. */
export interface ScreenFrame {
  x: number;
  y: number;
  width: number;
  height: number;
  screenWidth: number;
  screenHeight: number;
  /** The coordinate space input on this picture is expressed in, when it is not
   *  the screen's own: a browser's screencast arrives scaled down while its page
   *  takes input in its own CSS pixels. Zero means the screen's own size. */
  inputWidth: number;
  inputHeight: number;
  /** A keyframe covers the whole screen and can be painted with no history. */
  keyframe: boolean;
  /** Empty for a keepalive, which carries no picture. */
  jpeg: Uint8Array;
}

/**
 * Incremental parser over the byte stream.
 *
 * Chunk boundaries have nothing to do with frame boundaries - a single read can
 * deliver half a header or three frames - so bytes are buffered until a whole
 * frame is present. Anything less is kept for the next read rather than guessed
 * at.
 *
 * Chunks are held as they arrive and joined only when a frame is complete: a
 * 200 KiB keyframe crosses the network as dozens of chunks, and one
 * contiguous buffer per chunk would copy the whole pending frame each time,
 * on the thread the decode runs on.
 */
class ScreenFrameParser {
  private chunks: Uint8Array[] = [];
  private pending = 0;
  /** The header of the frame being assembled, once enough bytes exist to read
   *  it. Held so a frame split across many chunks parses its header once. */
  private header: FrameHeader | null = null;

  /** Feeds a chunk and returns every complete frame it finished. */
  push(chunk: Uint8Array): ScreenFrame[] {
    if (chunk.length > 0) {
      this.chunks.push(chunk);
      this.pending += chunk.length;
    }

    const frames: ScreenFrame[] = [];
    for (;;) {
      const frame = this.takeFrame();
      if (!frame) break;
      frames.push(frame);
    }
    return frames;
  }

  /** Removes and returns one frame, or null when only a partial is buffered. */
  private takeFrame(): ScreenFrame | null {
    if (this.header === null) {
      if (this.pending < FRAME_HEADER_LEN) return null;
      this.header = readHeader(this.peek(FRAME_HEADER_LEN));
    }
    const total = FRAME_HEADER_LEN + this.header.jpegLen;
    if (this.pending < total) return null;

    const whole = this.take(total);
    const frame: ScreenFrame = { ...this.header.fields, jpeg: whole.subarray(FRAME_HEADER_LEN) };
    this.header = null;
    return frame;
  }

  /** The first n buffered bytes, without consuming them. */
  private peek(n: number): Uint8Array {
    if (this.chunks.length > 0 && this.chunks[0].length >= n) return this.chunks[0];
    const out = new Uint8Array(n);
    let at = 0;
    for (const chunk of this.chunks) {
      if (at >= n) break;
      const part = chunk.subarray(0, Math.min(chunk.length, n - at));
      out.set(part, at);
      at += part.length;
    }
    return out;
  }

  /** Removes the first n buffered bytes and returns them contiguously. */
  private take(n: number): Uint8Array {
    const out = new Uint8Array(n);
    let at = 0;
    while (at < n) {
      const chunk = this.chunks[0];
      const want = n - at;
      if (chunk.length <= want) {
        out.set(chunk, at);
        at += chunk.length;
        this.chunks.shift();
        continue;
      }
      out.set(chunk.subarray(0, want), at);
      this.chunks[0] = chunk.subarray(want);
      at += want;
    }
    this.pending -= n;
    return out;
  }
}

/** A frame's fixed header: everything but the JPEG. */
interface FrameHeader {
  fields: Omit<ScreenFrame, 'jpeg'>;
  jpegLen: number;
}

function readHeader(bytes: Uint8Array): FrameHeader {
  const view = new DataView(bytes.buffer, bytes.byteOffset, FRAME_HEADER_LEN);
  return {
    fields: {
      keyframe: (view.getUint8(0) & 1) === 1,
      x: view.getUint16(1, false),
      y: view.getUint16(3, false),
      width: view.getUint16(5, false),
      height: view.getUint16(7, false),
      screenWidth: view.getUint16(9, false),
      screenHeight: view.getUint16(11, false),
      inputWidth: view.getUint16(13, false),
      inputHeight: view.getUint16(15, false),
    },
    jpegLen: view.getUint32(17, false),
  };
}

/** URL of a submission's live screen stream. */
function screenStreamURL(submissionId: string): string {
  return `/api/submissions/${submissionId}/screen`;
}

/**
 * What the live view is doing, in the words a viewer should read.
 *
 * 'waiting' is distinct from 'unavailable' on purpose: a sandbox that has not
 * started detonating yet HAS no screen, and saying "no live screen" there reads
 * as a fault when it is simply too early. 'finalizing' is the same argument at
 * the other end: the run is archiving and uploading, which is work in progress,
 * not the screen having failed. 'not-applicable' is the third: this run is on a
 * lane with no guest at all, so it is neither too early nor a fault, and it is
 * the one that must not be retried. 'busy' is the viewer limit: every live view
 * this caller may hold is open. It clears when one closes, so it is retried,
 * but the viewer is told to close one rather than shown a missing screen.
 */
export type ScreenStatus =
  | 'waiting'
  | 'connecting'
  | 'live'
  | 'finalizing'
  | 'unavailable'
  | 'busy'
  | 'not-applicable'
  | 'refused'
  | 'ended';

/**
 * Reads the stream, calling onFrame for each picture until the response ends or
 * the signal aborts.
 *
 * Keepalives are dropped here rather than passed on: they exist to keep proxies
 * from closing a connection to a guest that is drawing nothing, and carry no
 * information a viewer wants.
 *
 * Resolves with the reason the stream ended. A clean end is normal - it is what
 * happens when the analysis finishes - so it is a return value, not a throw.
 */
export async function readScreenStream(
  submissionId: string,
  onFrame: (frame: ScreenFrame) => void,
  signal: AbortSignal,
): Promise<ScreenStatus> {
  let resp: Response;
  try {
    resp = await fetch(screenStreamURL(submissionId), {
      credentials: 'include',
      signal,
    });
  } catch {
    return signal.aborted ? 'ended' : 'unavailable';
  }
  // 401/403/404 answer the same way on every retry: the session went, the
  // submission is not this caller's, or it is gone. Kept apart from the
  // transient refusals below because the viewer reconnects on those and must
  // stop on these - a tab left open past a session expiry otherwise reopens
  // this stream every three seconds for as long as it stays open.
  if (resp.status === 401 || resp.status === 403 || resp.status === 404) return 'refused';
  // 422 is "nothing here will ever paint" and is deliberately not
  // 'unavailable': unavailable means a picture could come from this machine and
  // is not, so the pane retries every three seconds. It is the ANALYZER's
  // answer rather than the lane's - a container run whose analyzer drives a
  // browser does have a screen, which is what the URL console shows.
  if (resp.status === 422) return 'not-applicable';
  if (resp.status === 429) return 'busy';
  // 409 (nothing running), 425 (the run is starting and its screen has not
  // arrived yet) and 503 (no screen is coming from that machine) are all "there
  // is no picture to show", which is what the pane says either way.
  if (!resp.ok || !resp.body) return 'unavailable';

  const reader = resp.body.getReader();
  const parser = new ScreenFrameParser();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) return 'ended';
      if (!value) continue;
      for (const frame of parser.push(value)) {
        if (frame.jpeg.length > 0) onFrame(frame);
      }
    }
  } catch {
    return signal.aborted ? 'ended' : 'unavailable';
  } finally {
    reader.cancel().catch(() => {});
  }
}
